import { useEffect, useMemo, useRef } from "react";
import { OptionItem, OptionsManagerData } from "../utils/fieldFormUtils";

const EMPTY_INITIAL_OPTIONS: string[] = ["", ""];

interface UseSeededLookupOptionsArgs {
  optionItems: OptionItem[];
  // Skip seeding while data isn't ready (still fetching, or no parent selected).
  skipSeed: boolean;
  onOptionsChange: (data: OptionsManagerData) => void;
  defaultValue?: string;
  // Parent/scope key folded into the seed guard so switching between parents
  // with identical items still re-seeds (else the stale parent stays in the form).
  scopeKey?: string | number | null;
}

/** Stable initial-option refs + seeds the form from the fetched options once data is ready. */
export const useSeededLookupOptions = ({
  optionItems,
  skipSeed,
  onOptionsChange,
  defaultValue = "",
  scopeKey = null
}: UseSeededLookupOptionsArgs) => {
  const hasItems = optionItems.length > 0;

  // Seed only when scope or content changes, so an identical refetch mid-edit
  // doesn't re-seed and clobber unsaved edits (a parent switch still re-seeds).
  const contentSignature = useMemo(
    () =>
      `${scopeKey ?? ""}::` +
      optionItems
        .map((item) => `${item.id ?? ""}:${item.name}:${item.isHidden ? 1 : 0}`)
        .join("|"),
    [optionItems, scopeKey]
  );

  const initialOptionsRef = useRef<string[]>(EMPTY_INITIAL_OPTIONS);
  const initialOptionItemsRef = useRef<OptionItem[] | undefined>(undefined);

  useEffect(() => {
    initialOptionsRef.current = hasItems
      ? optionItems.map((item) => item.name)
      : EMPTY_INITIAL_OPTIONS;
    initialOptionItemsRef.current = hasItems ? optionItems : undefined;
  }, [optionItems, hasItems]);

  const lastSeededSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (skipSeed) return;
    if (lastSeededSignatureRef.current === contentSignature) return;
    lastSeededSignatureRef.current = contentSignature;
    onOptionsChange({
      options: optionItems.map((item) => item.name),
      optionItems,
      allowUsersToAdd: false,
      defaultValue
    });
  }, [optionItems, contentSignature, skipSeed, onOptionsChange, defaultValue]);

  return {
    initialOptions: initialOptionsRef.current,
    initialOptionItems: initialOptionItemsRef.current,
    hasItems
  };
};
